import { Delta } from '@typewriter/delta';
import { inc } from 'alphacounter';
import { signal, writable } from 'easy-signal';
import { JSONPatch } from '../json-patch';
import logging from '../log';
import { EMPTY_OBJECT, EMPTY_REV, REV_LENGTH, pathExistsIn, textConversion } from '../util';
const log = logging.tagColor('Client', 'Green');
const applyOptions = { partial: true, createMissingObjects: true };
/**
 * Encapsulates the logic for keeping a document on the client up-to-date with changes from the worker and server. It
 * includes a signal that contains the value of the doc and methods for initial loading of the data and future updates
 * received in the form of change objects.
 *
 * For LWW docs, the syncedDoc and localDoc will always be the same. There is no need to keep 2 separate objects in
 * memory.
 */
export function getClientDoc(call, path, docConfig, syncs) {
    const trackLocalDoc = docConfig.type === 'operational_transformation';
    let configWithAccess = docConfig;
    while (!configWithAccess.access && configWithAccess.parent)
        configWithAccess = configWithAccess.parent;
    const access = docConfig.access;
    const { whitelist, blacklist, hasText } = docConfig;
    const isWritable = access === 'user_write' || access === 'role' || undefined;
    const isLoaded = writable(false);
    const hasLocalChanges = writable(false);
    const onChange = signal();
    // The onTextChange signal helps clients merge text changes into their local editor, dispatching deltas that have been
    // transformed all ready to be applied.
    const onTextChange = signal();
    let loading = null;
    let pendingTextChanges = null;
    // Synced version of the doc, committed on the server, we need this to reconstruct the local doc when receiving
    // new changes from the server
    let syncedDoc = EMPTY_OBJECT;
    let syncedRev = EMPTY_REV;
    // Local version of the doc, with local changes applied. If there are no local changes, it will be the same instance
    // as the synced doc. This is the value that is exposed to the client.
    /** A reactive signal containing the value of the current local doc */
    const { get, set, subscribe } = writable(syncedDoc);
    let localRev = EMPTY_REV;
    const localChanges = new Map();
    const localDoc = {
        get,
        subscribe,
        isLoaded,
        hasLocalChanges,
        onChange,
        onTextChange,
        sync: () => syncs.sync(path),
        unsync: () => syncs.unsync(path),
        load,
        unload,
        receiveDoc,
        receive,
        patch,
        update,
        delete: del,
    };
    return localDoc;
    /** Call to populate the initial data.  */
    async function load(forceReload) {
        if (loading)
            return loading;
        if (!forceReload && isLoaded.get())
            return localDoc.get();
        await localDoc.sync();
        isLoaded.set(false);
        return (loading = call('get', path).then(({ doc, rev, changes }) => {
            if (!loading)
                return null; // unload was called before the response was received
            loading = null;
            return receiveDoc(doc, rev, changes);
        }));
    }
    /** Unload the doc from memory */
    function unload() {
        loading = null;
        localChanges.clear();
        syncedRev = localRev = '';
        hasLocalChanges.set(false);
        isLoaded.set(false);
        set((syncedDoc = EMPTY_OBJECT));
    }
    /** Receive a complete doc update from the worker */
    function receiveDoc(doc, rev, changes) {
        if (!doc)
            doc = EMPTY_OBJECT;
        if (hasText)
            doc = textConversion.revive[hasText](doc);
        // Set the synced/committed version of the doc we have in our database and its revision
        syncedDoc = doc;
        syncedRev = rev;
        // Store the local changes by id (or rev for LWW) and apply them on top of the synced doc
        localChanges.clear();
        changes?.forEach(change => localChanges.set(change.id || change.rev, change));
        localRev = changes?.at(-1)?.rev || syncedRev;
        set(toPatch(localChanges).apply(syncedDoc, applyOptions));
        // Set the state of this client doc
        isLoaded.set(true);
        hasLocalChanges.set(!!localChanges.size);
        return localDoc.get();
    }
    /** Call when changes are received for this doc from the worker */
    function receive(changes, fromThisClient = false) {
        log('receive', changes[0].id ? changes.map(c => `${c.rev} | ${c.id}`) : changes.map(c => c.rev), fromThisClient ? '(local)' : '(remote)');
        const oldLocalIds = new Set(localChanges.keys());
        // Must be run before separateChanges adds changes to localChanges and docs get updated
        queueTriggerTextChanges(changes, fromThisClient);
        // Add local changes to our localChanges array and return committed changes for applying
        const committedChanges = separateChanges(changes);
        if (committedChanges === false) {
            // There is a gap in the changes, request changes (this shouldn't happen)
            localDoc.load(true);
            return;
        }
        if (!localChanges.size && committedChanges.every(c => oldLocalIds.has(c.id || c.rev))) {
            // All changes are already applied and were just synced to the server. Nothing new, just set syncedDoc to localDoc
            // as a shortcut and optimization
            syncedDoc = localDoc.get();
            syncedRev = localRev;
            return;
        }
        let oldDoc = localDoc.get();
        let newDoc = oldDoc;
        // Apply synced changes first on top of the synced doc
        if (committedChanges.length) {
            const patch = committedChanges.map(c => c.patch).flat();
            syncedRev = committedChanges[committedChanges.length - 1].rev;
            newDoc = syncedDoc = new JSONPatch(patch, hasText).apply(syncedDoc, applyOptions);
            log('syncedRev', syncedRev);
            const newChanges = committedChanges.filter(c => !oldLocalIds.has(c.id || c.rev));
            if (newChanges.length) {
                queueMicrotask(() => onChange(committedChanges, 'remote', newDoc, oldDoc));
            }
        }
        // Apply local changes on top of the synced doc to get the local doc
        if (localChanges.size) {
            const changes = [...localChanges.values()];
            const patch = toPatch(changes);
            localRev = changes.at(-1).rev;
            const newChanges = changes.filter(c => !oldLocalIds.has(c.id || c.rev));
            if (newChanges.length) {
                queueMicrotask(() => onChange(newChanges, fromThisClient ? 'this' : 'local', newDoc, oldDoc));
            }
            newDoc = patch.apply(syncedDoc, applyOptions);
        }
        else {
            localRev = syncedRev;
        }
        set(newDoc);
        log('localRev', syncedRev);
        hasLocalChanges.set(!!localChanges.size);
    }
    /** Call to apply changes to the local doc */
    async function patch(patch, pathPrefix = '') {
        if (!isWritable)
            throw new Error('This doc is read-only');
        patch = toOps(patch);
        if (!patch.length)
            return [];
        // Check if the patch is valid and can be applied to the object
        if (whitelist || blacklist) {
            patch.forEach(patch => {
                if (whitelist?.length && !pathExistsIn(patch.path, whitelist)) {
                    throw new TypeError(`${patch.path} is not a whitelisted property for this Syncable Object`);
                }
                if (blacklist?.length && pathExistsIn(patch.path, blacklist)) {
                    throw new TypeError(`${patch.path} is a blacklisted property for this Syncable Object`);
                }
            });
        }
        if (pathPrefix && pathPrefix[0] !== '/')
            pathPrefix = '/' + pathPrefix;
        if (pathPrefix)
            patch.forEach(op => (op.path = `${pathPrefix}${op.path}`));
        log('patch', patch[0].value);
        const changes = await call('patch', path, patch);
        receive(changes, true);
        return changes;
    }
    /** Call to apply changes to the local doc */
    async function update(updater, pathPrefix = '') {
        if (!isWritable)
            throw new Error('This doc is read-only');
        const patch = new JSONPatch([], hasText);
        if (typeof updater === 'function') {
            await updater(patch, localDoc.get());
        }
        else {
            let doc = localDoc.get();
            if (pathPrefix)
                pathPrefix
                    .split('/')
                    .slice(1)
                    .forEach(key => (doc = doc?.[key]));
            patchFromUpdate(patch, doc, updater);
        }
        return localDoc.patch(patch, pathPrefix);
    }
    /** Call to apply changes to the local doc */
    async function del() {
        if (!isWritable)
            throw new Error('This doc is read-only');
        await call('delete', path);
    }
    /**
     * Take the changes and split them into committed/synced changes and local changes. The local changes are set to
     * localChanges and the committed changes are returned. If we don't track local changes, all changes are returned.
     */
    function separateChanges(changes) {
        if (!trackLocalDoc) {
            return changes;
        }
        // Anything before the synced rev is already applied
        changes = changes.filter(c => !c.rev || c.rev > syncedRev);
        if (!changes.length)
            return changes;
        // Request to reload the data if there is a gap in what we have and what is being received
        if (changes[0].rev) {
            const revToCheck = changes[0].committed ? syncedRev : localRev;
            if (changes[0].rev !== inc(revToCheck, REV_LENGTH)) {
                console.error('There is a gap in the changes, request a reload', 'syncedRev:', syncedRev, 'localRev:', localRev, 'changes:', changes);
                return false;
            }
        }
        changes = changes.filter(c => {
            if (c.committed) {
                // Remove any local changes that are now committed
                localChanges.delete(c.id || c.rev);
                return true;
            }
            localChanges.set(c.id || c.rev, c);
        });
        return changes;
    }
    function queueTriggerTextChanges(receivedChanges, fromThisClient) {
        // No need to transform our own changes, send them to the client immediately
        if (fromThisClient) {
            receivedChanges.forEach(c => {
                c.patch.forEach(op => {
                    if (op.op === '@changeText') {
                        queueTextChange(op.path, new Delta(op.value), fromThisClient);
                    }
                });
            });
            return;
        }
        // 1. go through received changes and find those with text changes that are not already in the local changes
        const receivedDeltas = new Map();
        const receivedIds = new Set();
        receivedChanges.forEach(c => {
            if (c.id && localChanges.has(c.id))
                return;
            c.patch.forEach(op => {
                if (op.op !== '@changeText')
                    return;
                if (c.id)
                    receivedIds.add(c.id);
                const changeDelta = new Delta(op.value);
                let delta = receivedDeltas.get(op.path);
                delta = delta ? delta.compose(changeDelta) : changeDelta;
                receivedDeltas.set(op.path, delta);
            });
        });
        if (!receivedDeltas.size)
            return;
        // 2. get our local changes with text changes on the same path as received ones that aren't in the received changes
        const localDeltas = new Map();
        localChanges.forEach(c => {
            if (c.id && receivedIds.has(c.id))
                return;
            c.patch.forEach(op => {
                if (op.op !== '@changeText')
                    return;
                let delta = localDeltas.get(op.path);
                delta = delta ? delta.compose(op.value) : op.value;
                localDeltas.set(op.path, delta);
            });
        });
        // 3. transform the received changes against our local changes that were before the received changes
        receivedDeltas.forEach((delta, path) => {
            const localDelta = localDeltas.get(path);
            if (localDelta) {
                delta = localDelta.transform(delta, true);
            }
            // 4. send those received changes to the client with onTextChange
            queueTextChange(path, delta, fromThisClient);
        });
    }
    function queueTextChange(path, delta, fromThisClient) {
        if (!pendingTextChanges) {
            pendingTextChanges = [];
            queueMicrotask(() => {
                if (!pendingTextChanges)
                    return;
                const changes = pendingTextChanges;
                pendingTextChanges = null;
                changes.forEach(({ path, delta, fromThisClient }) => onTextChange(path, delta, fromThisClient));
            });
        }
        pendingTextChanges.push({ path, delta, fromThisClient });
    }
    function toPatch(changes) {
        let ops;
        if (changes instanceof Map) {
            ops = [];
            changes.forEach(c => ops.push(...c.patch));
        }
        else {
            ops = changes.map(c => c.patch).flat();
        }
        return new JSONPatch(ops, hasText);
    }
}
function toOps(patch) {
    return patch.ops || patch;
}
/**
 * Recursively creates a patch from an object that represents the desired state of the document.
 */
function patchFromUpdate(patch, doc, target, path = '') {
    for (const [key, value] of Object.entries(target)) {
        if (doc?.[key] !== value) {
            if (value == null)
                patch.remove(`/${key}`);
            else if (typeof value === 'object' && !Array.isArray(value))
                patchFromUpdate(patch, doc?.[key], value, `${path}/${key}`);
            else
                patch.replace(`${path}/${key}`, value);
        }
    }
}
